import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { airports } from "@/data/airports";
import { airportDetails } from "@/data/airportDetails";
import { getTerminalInfo, getTerminalGroups } from "@/data/airlineTerminals";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Clock, Info, ExternalLink } from "lucide-react";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import {
  SchemaMarkup,
  generateBreadcrumbSchema,
  generateFAQSchema,
} from "@/components/SchemaMarkup";
import { LiveWaitStrip } from "@/components/airport/LiveWaitStrip";
import { AppStoreCTA } from "@/components/AppStoreCTA";
import NotFound from "./NotFound";

/**
 * /airports/:code/precheck — targets "tsa precheck at atl", "tsa pre check at jfk"
 * and similar. Everything on this page comes from the curated airport data or
 * from TSA's own published programme rules; nothing about lane locations is
 * invented, and travellers are pointed at the official checkpoint list.
 */
const AirportPreCheck = () => {
  const { code } = useParams<{ code: string }>();
  const upper = code?.toUpperCase() ?? "";
  const lower = code?.toLowerCase() ?? "";

  const airport = airports.find((a) => a.code === upper);
  const details = airportDetails[upper];

  if (!airport || !details) return <NotFound />;

  const cityName = airport.city.split(",")[0];
  const url = `https://airportbuddy.app/airports/${lower}/precheck`;
  const terminalInfo = getTerminalInfo(upper);
  const terminalGroups = getTerminalGroups(upper);

  const preCheck = details.preCheckLanes;
  const clear = details.clearLanes;

  const faqs = [
    {
      question: `Is there TSA PreCheck at ${upper}?`,
      answer: preCheck
        ? `Yes. ${airport.name} has TSA PreCheck lanes. Lane opening hours vary by checkpoint and are usually shorter than standard lanes early in the morning and late at night — when a PreCheck lane is closed you are directed to the standard line.`
        : `${airport.name} is not listed with dedicated TSA PreCheck lanes in our data. Check the official TSA checkpoint list before you fly, as availability changes.`,
    },
    {
      question: `Is CLEAR available at ${upper}?`,
      answer: clear
        ? `Yes. CLEAR operates at ${airport.name}. CLEAR verifies your identity so you skip the document-check queue, then you join either the PreCheck or standard screening lane.`
        : `CLEAR is not listed at ${airport.name} in our data. CLEAR adds locations regularly, so confirm on their site before relying on it.`,
    },
    {
      question: `How much time does PreCheck save at ${upper}?`,
      answer: `Standard screening at ${upper} typically runs ${details.avgDomesticWait} for domestic departures and ${details.avgInternationalWait} for international. PreCheck lanes are usually a fraction of that, but the saving collapses when the PreCheck lane itself backs up at peak — check the live wait on this page before you decide which lane to head for.`,
    },
    {
      question: `When is ${upper} busiest?`,
      answer: `Security peaks at ${upper} run ${details.peakHours.morning} and ${details.peakHours.evening}, and the heaviest days are ${details.peakHours.busiestDays.join(", ")}.`,
    },
  ];

  return (
    <>
      <Helmet>
        <title>{`TSA PreCheck at ${upper} – Lanes, CLEAR & Live Wait Times`}</title>
        <meta
          name="description"
          content={`TSA PreCheck at ${airport.name} (${upper}): whether PreCheck and CLEAR lanes are available, how much time they save, peak hours, and the live security wait right now.`}
        />
        <link rel="canonical" href={url} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={`TSA PreCheck at ${upper} – Lanes & Live Wait Times`} />
        <meta
          property="og:description"
          content={`PreCheck and CLEAR availability at ${airport.name}, plus how long security is taking right now.`}
        />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`TSA PreCheck at ${upper}`} />
        <meta
          name="twitter:description"
          content={`PreCheck and CLEAR at ${airport.name}, with the live security wait.`}
        />
      </Helmet>

      <SchemaMarkup
        schema={generateBreadcrumbSchema([
          { name: "Home", url: "/" },
          { name: "Airports", url: "/airports" },
          { name: upper, url: `/airports/${lower}` },
          { name: "TSA PreCheck", url: `/airports/${lower}/precheck` },
        ])}
      />
      <SchemaMarkup schema={generateFAQSchema(faqs)} />

      <div className="min-h-screen bg-gradient-to-br from-background via-secondary/5 to-accent/10">
        <div className="container mx-auto px-4 pt-4 pb-12">
          <Breadcrumbs
            items={[
              { label: "Home", href: "/" },
              { label: "Airports", href: "/airports" },
              { label: upper, href: `/airports/${lower}` },
              { label: "TSA PreCheck", href: `/airports/${lower}/precheck` },
            ]}
          />

          <div className="max-w-3xl mx-auto space-y-6">
            <header className="space-y-2 mt-2">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">
                TSA PreCheck at {upper}
              </h1>
              <p className="text-base md:text-lg text-muted-foreground">
                {airport.name} · {cityName}
              </p>
            </header>

            {/* The answer, immediately */}
            <Card className="rounded-xl shadow-md border-primary/30">
              <CardContent className="py-6 space-y-3">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="h-7 w-7 text-primary shrink-0 mt-1" aria-hidden="true" />
                  <div>
                    <p className="text-2xl font-bold text-foreground">
                      {preCheck ? `Yes — ${upper} has TSA PreCheck lanes` : `PreCheck lanes not listed at ${upper}`}
                    </p>
                    <p className="text-base text-muted-foreground">
                      {clear ? "CLEAR is also available here." : "CLEAR is not listed at this airport."}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Badge variant={preCheck ? "default" : "secondary"}>
                    {preCheck ? "TSA PreCheck" : "No PreCheck listed"}
                  </Badge>
                  <Badge variant={clear ? "default" : "secondary"}>
                    {clear ? "CLEAR" : "No CLEAR listed"}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground border-t pt-3">
                  Lane availability and opening hours change. Confirm on{" "}
                  <a
                    href="https://www.tsa.gov/precheck"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline inline-flex items-center gap-1"
                  >
                    TSA's official PreCheck page
                    <ExternalLink className="h-3 w-3" aria-hidden="true" />
                  </a>
                  .
                </p>
              </CardContent>
            </Card>

            <LiveWaitStrip airportCode={upper} />

            {/* Time saved */}
            <Card className="rounded-xl shadow-md">
              <CardContent className="py-6 space-y-3">
                <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
                  <Clock className="h-5 w-5 text-primary shrink-0" aria-hidden="true" />
                  How much time PreCheck saves at {upper}
                </h2>
                <ul className="space-y-2">
                  <li className="text-base text-foreground">
                    Standard domestic screening: <strong>{details.avgDomesticWait}</strong>
                  </li>
                  <li className="text-base text-foreground">
                    International departures: <strong>{details.avgInternationalWait}</strong>
                  </li>
                  <li className="text-base text-foreground">
                    Busiest windows: <strong>{details.peakHours.morning}</strong> and{" "}
                    <strong>{details.peakHours.evening}</strong>
                  </li>
                </ul>
                <p className="text-base text-muted-foreground">
                  PreCheck usually clears well inside those times, but on the heaviest mornings the
                  PreCheck queue at {upper} can grow too. The live figure above is the one to trust.
                </p>
              </CardContent>
            </Card>

            {/* Security tips */}
            {details.tipsSecurity?.length ? (
              <Card className="rounded-xl shadow-md">
                <CardContent className="py-6 space-y-2">
                  <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
                    <Info className="h-5 w-5 text-primary shrink-0" aria-hidden="true" />
                    Checkpoint tips for {upper}
                  </h2>
                  <ul className="space-y-1">
                    {details.tipsSecurity.map((tip) => (
                      <li key={tip} className="text-base text-muted-foreground">
                        • {tip}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ) : null}

            <AppStoreCTA
              placement="airport_hero"
              context={upper}
              headline={`PreCheck or not, timing still decides it`}
              subline={`Airport Buddy watches the ${upper} security line and pushes you a leave-by reminder. Free on iPhone.`}
            />

            {/* Terminals */}
            {terminalGroups.length > 0 && (
              <Card className="rounded-xl shadow-md">
                <CardContent className="py-6 space-y-4">
                  <h2 className="text-xl font-bold text-foreground">
                    Checkpoints by terminal at {upper}
                  </h2>
                  {terminalInfo && (
                    <p className="text-base text-muted-foreground">{terminalInfo.connection}</p>
                  )}
                  <ul className="grid sm:grid-cols-2 gap-2">
                    {terminalGroups.map((g) => (
                      <li key={g.slug}>
                        <Link
                          to={`/airports/${lower}/terminals/${g.slug}`}
                          className="flex items-center justify-between gap-2 rounded-xl border px-4 py-3 min-h-[44px] text-base hover:bg-accent/50 active:scale-95 transition"
                        >
                          <span className="font-medium">{g.label}</span>
                          <Badge variant="secondary">
                            {g.airlines.length} airline{g.airlines.length === 1 ? "" : "s"}
                          </Badge>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* FAQ */}
            <Card className="rounded-xl shadow-md">
              <CardContent className="py-6 space-y-5">
                <h2 className="text-xl font-bold text-foreground">
                  TSA PreCheck at {upper}: common questions
                </h2>
                {faqs.map((f) => (
                  <div key={f.question} className="space-y-1">
                    <h3 className="text-base font-semibold text-foreground">{f.question}</h3>
                    <p className="text-base text-muted-foreground">{f.answer}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild className="min-h-[44px] rounded-xl active:scale-95 flex-1">
                <Link to={`/airports/${lower}`}>{upper} live wait times</Link>
              </Button>
              <Button asChild variant="outline" className="min-h-[44px] rounded-xl active:scale-95 flex-1">
                <Link to={`/airports/${lower}/terminals`}>{upper} terminals</Link>
              </Button>
              <Button asChild variant="outline" className="min-h-[44px] rounded-xl active:scale-95 flex-1">
                <Link to="/tips/minimize-wait-times">Clear security faster</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AirportPreCheck;
